/**
 * Tabela de rotas autenticadas. Cada página entra com o Layout e a guarda de
 * login; rotas antigas que tinham favoritos redirecionam para a tela nova.
 */
import type { ReactNode } from 'react'
import { CalendarPage } from '@/pages/CalendarPage'
import { CarteirasSettingsPage } from '@/pages/CarteirasSettingsPage'
import { CaseDetailsPage } from '@/pages/CaseDetailsPage'
import { CasesPage } from '@/pages/CasesPage'
import { ClientDetailsPage } from '@/pages/ClientDetailsPage'
import { ClientsPage } from '@/pages/ClientsPage'
import { ClientsReportsPage } from '@/pages/ClientsReportsPage'
import { ConvertCaseToLegalPage } from '@/pages/ConvertCaseToLegalPage'
import { CreateCasePage } from '@/pages/CreateCasePage'
import { CreateClientPage } from '@/pages/CreateClientPage'
import { CreateInteractionPage } from '@/pages/CreateInteractionPage'
import { EditCasePage } from '@/pages/EditCasePage'
import { EditClientPage } from '@/pages/EditClientPage'
import { FinanceV2AccountsPage } from '@/pages/FinanceV2AccountsPage'
import FinanceV2AnnualFeesPage from '@/pages/FinanceV2AnnualFeesPage'
import { FinanceV2BtgConfigPage } from '@/pages/FinanceV2BtgConfigPage'
import { FinanceV2CarneGenerationPage } from '@/pages/FinanceV2CarneGenerationPage'
import { FinanceV2CategoriesPage } from '@/pages/FinanceV2CategoriesPage'
import FinanceV2CollectionPage from '@/pages/FinanceV2CollectionPage'
import { FinanceV2CostCentersPage } from '@/pages/FinanceV2CostCentersPage'
import { FinanceV2DashboardPage } from '@/pages/FinanceV2DashboardPage'
import { FinanceV2InvoiceablePage } from '@/pages/FinanceV2InvoiceablePage'
import { FinanceV2InvoicesPage } from '@/pages/FinanceV2InvoicesPage'
import FinanceV2RecurringPage from '@/pages/FinanceV2RecurringPage'
import { FinanceV2ReportsPage } from '@/pages/FinanceV2ReportsPage'
import { FinanceV2ServiceTypesPage } from '@/pages/FinanceV2ServiceTypesPage'
import { FinanceV2SimplifiedPage } from '@/pages/FinanceV2SimplifiedPage'
import { FinanceV2TimesheetsPage } from '@/pages/FinanceV2TimesheetsPage'
import { FinanceV2TransactionsPage } from '@/pages/FinanceV2TransactionsPage'
import { FinanceV2TransfersPage } from '@/pages/FinanceV2TransfersPage'
import { ForensicCalendarPage } from '@/pages/ForensicCalendarPage'
import { HojePage } from '@/pages/HojePage'
import { InteractionDetailsPage } from '@/pages/InteractionDetailsPage'
import { InteractionsPage } from '@/pages/InteractionsPage'
import { LeadsPage } from '@/pages/LeadsPage'
import { NotificationsPage } from '@/pages/NotificationsPage'
import { ProfilePage } from '@/pages/ProfilePage'
import { PublicationImportsPage } from '@/pages/PublicationImportsPage'
import { PublicationMonitorsPage } from '@/pages/PublicationMonitorsPage'
import { PublicationsInboxPage } from '@/pages/PublicationsInboxPage'
import { PublicationsPage } from '@/pages/PublicationsPage'
import { ReportsPage } from '@/pages/ReportsPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { TarefasPage } from '@/pages/TarefasPage'
import { TaskDashboard } from '@/pages/TaskDashboard'
import { TeamSettingsPage } from '@/pages/TeamSettingsPage'
import { UsersPage } from '@/pages/UsersPage'
import BackupPage from '@/pages/BackupPage'
import { FinanceFeatureGate } from '@/components/finance-v2/FinanceFeatureGate'
import { FinanceViewModeProvider } from '@/contexts/FinanceViewModeContext'
import type { FinanceFeature } from '@/lib/finance'

export interface PageRoute {
  path: string
  element: ReactNode
  /** Permissão `recurso:ação` exigida pela guarda (ProtectedRoute). */
  permission?: string
}

/** Aterrissagem depois do login: a lista de clientes (decisão do Paulo, 29/10/2026). */
export const HOME_PATH = '/clients'

const gated = (feature: FinanceFeature, page: ReactNode) => <FinanceFeatureGate feature={feature}>{page}</FinanceFeatureGate>

export const PAGE_ROUTES: readonly PageRoute[] = [
  { path: '/hoje', element: <HojePage /> },

  { path: '/publications', element: <PublicationsInboxPage /> },
  { path: '/publications/importacoes', element: <PublicationImportsPage /> },
  { path: '/publications/monitorados', element: <PublicationMonitorsPage /> },
  // Painel antigo (métricas, gráficos e ações em lote), linkado da caixa de entrada
  { path: '/publications/painel', element: <PublicationsPage /> },

  // Tarefas: uma tela de vistas + gaveta única (?tarefa=<id>|nova)
  { path: '/tasks', element: <TarefasPage /> },
  { path: '/tasks/dashboard', element: <TaskDashboard /> },

  { path: '/calendar', element: <CalendarPage /> },

  { path: '/cases', element: <CasesPage /> },
  { path: '/cases/new', element: <CreateCasePage /> },
  { path: '/cases/:id', element: <CaseDetailsPage /> },
  { path: '/cases/:id/edit', element: <EditCasePage /> },
  { path: '/cases/:id/convert-to-legal', element: <ConvertCaseToLegalPage /> },

  { path: '/clients', element: <ClientsPage /> },
  { path: '/clients/new', element: <CreateClientPage /> },
  { path: '/clients/:id', element: <ClientDetailsPage /> },
  { path: '/clients/:id/edit', element: <EditClientPage /> },
  { path: '/leads', element: <LeadsPage /> },

  { path: '/interactions', element: <InteractionsPage /> },
  { path: '/interactions/new', element: <CreateInteractionPage /> },
  { path: '/interactions/:id', element: <InteractionDetailsPage /> },

  { path: '/finance-v2/simplified', element: <FinanceViewModeProvider><FinanceV2SimplifiedPage /></FinanceViewModeProvider> },
  { path: '/finance-v2/dashboard', element: <FinanceV2DashboardPage /> },
  { path: '/finance-v2/accounts', element: <FinanceV2AccountsPage /> },
  { path: '/finance-v2/transactions', element: <FinanceV2TransactionsPage /> },
  { path: '/finance-v2/transfers', element: gated('transfers', <FinanceV2TransfersPage />) },
  { path: '/finance-v2/categories', element: <FinanceV2CategoriesPage /> },
  { path: '/finance-v2/cost-centers', element: <FinanceV2CostCentersPage /> },
  { path: '/finance-v2/reports', element: <FinanceV2ReportsPage /> },
  { path: '/finance-v2/btg-config', element: gated('btg', <FinanceV2BtgConfigPage />) },
  { path: '/finance-v2/carne', element: gated('carne', <FinanceV2CarneGenerationPage />) },
  { path: '/finance-v2/invoices', element: gated('invoices', <FinanceV2InvoicesPage />) },
  { path: '/finance-v2/invoices/to-invoice', element: gated('invoices', <FinanceV2InvoiceablePage />) },
  { path: '/finance-v2/service-types', element: gated('invoices', <FinanceV2ServiceTypesPage />) },
  { path: '/finance-v2/timesheets', element: gated('invoices', <FinanceV2TimesheetsPage />) },
  { path: '/finance-v2/annual-fees', element: <FinanceV2AnnualFeesPage /> },
  { path: '/finance-v2/recurring', element: <FinanceV2RecurringPage /> },
  { path: '/finance-v2/collection', element: <FinanceV2CollectionPage /> },

  { path: '/settings', element: <SettingsPage /> },
  { path: '/settings/equipe', element: <TeamSettingsPage /> },
  { path: '/settings/calendario-forense', element: <ForensicCalendarPage /> },
  { path: '/settings/carteiras', element: <CarteirasSettingsPage /> },
  { path: '/users', element: <UsersPage /> },
  { path: '/reports', element: <ReportsPage /> },
  { path: '/reports/clients', element: <ClientsReportsPage /> },
  { path: '/backup', element: <BackupPage />, permission: 'system:admin' },

  { path: '/notifications', element: <NotificationsPage /> },
  { path: '/profile', element: <ProfilePage /> },
]

/**
 * Rotas antigas → tela nova. Dashboard e Área de Trabalho deram lugar ao Hoje;
 * a tela DJEN deu lugar à caixa de entrada.
 */
export const REDIRECTS: ReadonlyArray<{ from: string; to: string }> = [
  { from: '/', to: HOME_PATH },
  { from: '/dashboard', to: HOME_PATH },
  { from: '/workspace', to: HOME_PATH },
  { from: '/communications', to: '/publications' },
  { from: '/finance-v2', to: '/finance-v2/simplified' },
]
